// Tiny fetch wrapper. All API responses are JSON. On 401, redirect to /login.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Every request is bounded. Without a timeout a stalled connection never settles, so the
// caller sits on its loading state forever with nothing on screen to explain it. The default
// is deliberately generous because some calls are genuinely slow (the collection import walks
// every row one at a time); callers that gate the whole UI — the auth calls — pass a shorter
// timeoutMs of their own. Status 0 means "no reply at all", never a response from the server.
const DEFAULT_TIMEOUT_MS = 120_000;

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...rest } = init;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(rest.headers || {}),
      },
      signal: ctl.signal,
      ...rest,
    });
  } catch {
    throw new ApiError(0, null, ctl.signal.aborted
      ? `The server didn't respond within ${Math.round(timeoutMs / 1000)}s. Check your connection and try again.`
      : "Couldn't reach the server. Check your connection and try again.");
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/api/auth")) {
      window.location.href = "/login";
    }
    const msg =
      typeof body === "object" && body && "error" in body
        ? String((body as Record<string, unknown>).error)
        : `Request failed with ${res.status}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}
